import { Field } from 'Components/Core';
import { IFieldProps } from 'Components/Core/Field/types';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ChangeEventWithData } from 'Components/Core/types';
import EquipmentTypes from 'Components/Equipment/equipmentTypes';
import { ListDropdown } from 'Components/List';
import { IWorksheetEquipmentDropdownProps } from 'Components/Worksheets/Core';
import { SetValuePayLoad } from 'Components/Worksheets/Core/WorksheetEditorInjectedPropsProvider/types';
import { hasValue } from 'Lib/Any';
import { useHasChangedAfterFirstLoad } from 'Lib/Hooks';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

type WorksheetBatchUpdateEquipmentSelectionFieldProps = Omit<IFieldProps, 'input'> &
  Pick<IWorksheetEquipmentDropdownProps, 'equipmentType' | 'maxResults'> & {
    onChange?: (e: ChangeEventWithData) => void;
    setValue?: (p: SetValuePayLoad) => void;
  };

/**
 * Provides a field for selecting an equipment item
 *
 * Currently the equipment selection is always retrieved for the laboratory of the first worksheet loaded
 * (we only support loading worksheets on the same work order)
 */
let WorksheetBatchUpdateEquipmentSelectionField: React.FC<WorksheetBatchUpdateEquipmentSelectionFieldProps> = ({
  value,
  name,
  equipmentType,
  maxResults = 20,
  disabled,
  messages,
  setValue,
  onChange,
  ...otherProps
}) => {
  if (!equipmentType)
    throw new Error('Component WorksheetBatchUpdateEquipmentSelectionField requires an equipmentType to be specified.');

  const refreshingWorksheets = useSelector((state) => state.worksheetBatchUpdate.status.refreshingWorksheets);

  // Fetch equipment for the laboratory of the first worksheet
  // NOTE: Currently worksheets can only be loaded for the same work order, so they are always in the same lab context.
  const laboratoryId = useSelector(
    (state) =>
      Object.values(state.worksheetBatchUpdate.worksheets).find((_) => true)?.syncModel_synced.header.Laboratory.Id
  );

  // If the laboratory id changes, clear selection as it may not be valid for the new worksheet.
  const laboratoryIdHasChanged = useHasChangedAfterFirstLoad(laboratoryId);

  useEffect(() => {
    if (laboratoryIdHasChanged) {
      setValue!({ name: name!, value: undefined });
    }
  }, [laboratoryIdHasChanged, name, setValue]);

  if (!hasValue(laboratoryId))
    messages?.push({
      message: 'Worksheets must be loaded before equipment can be selected.',
      severity: 'info',
    });
  const endpoint = (EquipmentTypes as any)[equipmentType]?.endpoint ?? 'list.equipment';

  return (
    <Field
      {...otherProps}
      input={<ListDropdown />}
      disabled={disabled || refreshingWorksheets || !hasValue(laboratoryId)}
      placeholder={!hasValue(laboratoryId) ? '' : undefined}
      endpoint={endpoint}
      maxResults={maxResults}
      queryParams={{ equipmentType, laboratoryId, limit: maxResults }}
      simpleValue
      // Include laboratory id in key so component is remounted when it has changed.
      // TODO: This is a workaround for problems with the ListDropdown component where it doesn't check if it has a valid
      // set of query parameters before trying to run the query
      key={`${name}_${laboratoryId}`}
      value={value?.Id ?? null}
      messages={messages}
      searchImmediately={hasValue(laboratoryId)}
      onChange={(e: ChangeEventWithData) => {
        onChange!({
          ...e,
          data: {
            ...e.data,
            value: hasValue(e.data?.value) ? { Id: e.data!.value } : null,
          },
        });
      }}
    />
  );
};

WorksheetBatchUpdateEquipmentSelectionField = withInjectedProps(WorksheetBatchUpdateEquipmentSelectionField);

export default WorksheetBatchUpdateEquipmentSelectionField;
export { WorksheetBatchUpdateEquipmentSelectionField };
